// useStageMoveToasts.js — the app-level subscriber that turns stage moves into
// one Undo toast (Phase 55: D-12, UI-SPEC section 8).
//
// Every stage move is emitted on stageMoveNotifier; moves that land together
// (a bulk status change) arrive here as one batch, so a burst raises ONE
// summary toast and its Undo reverts every move in it.
//
// Review D-WR-09: a batch that arrives while an earlier batch's toast is still
// showing is MERGED into it (the toast is replaced by dedupeKey, so a separate
// toast would silently drop the earlier batch's Undo). The copy counts each
// contact once, by its net move.

import React from 'react';
import { stageMoveNotifier } from '../services/stageMoveNotifier';
import { undoStageMoves } from '../services/contactStageService';
import { contactStageLabel } from '../config/contactPipeline';

export const STAGE_MOVE_TOAST_DURATION_MS = 8000;
const UNDO_FAILED_MESSAGE = "Couldn't undo. Check your connection and try again.";
export const UNDO_NOTHING_MESSAGE = 'Nothing to undo. The stage has changed since.';

// One entry per contact: the first move's fromStage and the last move's
// toStage, in the order each contact first moved.
export function netStageMoves(moves) {
    const byContact = new Map();
    for (const m of moves) {
        const key = `${m.workspaceId}/${m.contactId}`;
        const seen = byContact.get(key);
        if (seen) byContact.set(key, { ...m, fromStage: seen.fromStage });
        else byContact.set(key, m);
    }
    return [...byContact.values()];
}

// Review C-WR-10: a move whose timeline entry failed still landed, so it keeps
// its Undo toast; the note is quiet and never an error.
export const TIMELINE_NOT_SAVED_NOTE = "The timeline entry didn't save.";

function moveSummary(net) {
    if (net.length === 1) {
        return `${net[0].contactName} moved to ${contactStageLabel(net[0].toStage)}`;
    }
    const sameStage = net.every((m) => m.toStage === net[0].toStage);
    return sameStage
        ? `${net.length} contacts moved to ${contactStageLabel(net[0].toStage)}`
        : `${net.length} contacts changed stage`;
}

export function stageMoveMessage(moves) {
    const summary = moveSummary(netStageMoves(moves));
    return moves.some((m) => m && m.logFailed) ? `${summary}. ${TIMELINE_NOT_SAVED_NOTE}` : summary;
}

export function stageUndoMessage(moves) {
    const net = netStageMoves(moves);
    return net.length === 1
        ? `${net[0].contactName} is back in ${contactStageLabel(net[0].fromStage)}`
        : `${net.length} contacts are back where they were`;
}

export function useStageMoveToasts({ showToast, actor }) {
    // Held in refs so a new showToast or actor identity never resubscribes.
    const showToastRef = React.useRef(showToast);
    const actorRef = React.useRef(actor);
    showToastRef.current = showToast;
    actorRef.current = actor;

    // The moves behind the toast that is showing now, or null.
    const visibleRef = React.useRef(null);

    React.useEffect(() => {
        const unsubscribe = stageMoveNotifier.subscribe((incoming) => {
            if (!Array.isArray(incoming) || incoming.length === 0) return;
            const now = Date.now();
            const visible = visibleRef.current;
            const stillShowing = visible && !visible.undone
                && now - visible.shownAt < STAGE_MOVE_TOAST_DURATION_MS;
            const moves = stillShowing ? [...visible.moves, ...incoming] : incoming;
            const batch = { moves, shownAt: now, undone: false };
            visibleRef.current = batch;
            const onUndo = async () => {
                batch.undone = true;
                try {
                    const result = await undoStageMoves(moves, { actor: actorRef.current });
                    if (result && result.restored === 0 && result.skipped > 0) {
                        showToastRef.current(UNDO_NOTHING_MESSAGE, 'info');
                    } else {
                        showToastRef.current(stageUndoMessage(moves), 'success');
                    }
                } catch (error) {
                    console.error('Stage move undo failed:', error);
                    showToastRef.current(UNDO_FAILED_MESSAGE, 'error');
                }
            };
            showToastRef.current(stageMoveMessage(moves), 'info', {
                action: { label: 'Undo', onClick: onUndo },
                duration: STAGE_MOVE_TOAST_DURATION_MS,
                dedupeKey: 'stage-move',
            });
        });
        return unsubscribe;
    }, []);
}

export default useStageMoveToasts;
